import React, { useState } from 'react';
import { PrioritiesMatrix } from 'components/common/PrioritiesMatrix/PrioritiesMatrix';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { LoaderContainer } from 'components/common';
import {
    PRIORITIES_MATRIX_COMPONENT_HEIGHT,
    PRIORITIES_MATRIX_FILTERS,
} from 'components/widgets/PrioritiesMatrix/prioritiesMatrixComponent.utils';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import {
    IComponentProps,
    ILocalFilters,
    ITypeAndRole,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import NoData from 'components/common/NoData/noData';
import { SubProgramPreferencesLink } from 'components/common/SubProgramPreferencesLink/subProgramPreferencesLink';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { List } from 'components/common/ListWrapper/list';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { useQueryState } from 'hooks/useQueryState';
import { usePrioritiesMatrixComponentProvider } from 'components/widgets/PrioritiesMatrix/PrioritiesMatrixComponent.provider';
import { useBaseWidget } from 'hooks/useBaseWidget';
import { usePrioritiesMatrixLogic } from 'components/widgets/PrioritiesMatrix/prioritiesMatrixComponent.logic';

export interface LocalFilters extends ILocalFilters {
    tagsQuery: { tags: null | string; excludeTags: null | string } | null;
}

export interface Props extends IComponentProps {
    localFilters: LocalFilters;
}

const title = 'Priorities Matrix';
const componentKey = 'prioritiesMatrix';
const id = 'prioritiesMatrix';
const MATRIX_PROGRAM_ACTIVITIES_DEFAULT_ID = 3;

const PrioritiesMatrixComponent = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    localFilters,
    setSubTitleForExport,
}: Props) => {
    // state
    const [matrixCardCategoryId, setMatrixCardCategoryId] = useState<number>(MATRIX_PROGRAM_ACTIVITIES_DEFAULT_ID);
    const [matrixFilterUrlParam, setMatrixFilterUrlParam] = useQueryState('matrixFilter');
    const [isOverdueFilter, setIsOverdueFilter] = useState<any>(false);
    const [isDelayDriverFilter, setIsDelayDriverFilter] = useState<any>(false);
    const [isAssignedToMeFilter, setIsAssignedToMeFilter] = useState<any>(false);
    const [isCriticalPathFilter, setIsCriticalPathFilter] = useState<any>(false);
    const [selectedTypeAndRole, setSelectedTypeAndRole] = useState<ITypeAndRole>({
        type: 'doNowComponent',
        role: 'itemProgramActivities',
    });

    const { ui, api } = usePrioritiesMatrixComponentProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        matrixFilterUrlParam,
        matrixCardCategoryId,
        localFilters,
        isOverdueFilter,
        isDelayDriverFilter,
        isAssignedToMeFilter,
        isCriticalPathFilter,
        selectedTypeAndRole,
    });

    const { setFilterTitle, filtersTitle, handleFilterClick, urgentFilter, linkToProjectActivities, fetchCardsData } =
        usePrioritiesMatrixLogic({
            ui,
            api,
            widgetId,
            setMatrixFilter: setMatrixFilterUrlParam,
            matrixFilter: matrixFilterUrlParam,
            setMatrixCardCategoryId,
            isAssignedToMeFilter,
        });

    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    return (
        <WidgetWithTitle
            title={title}
            tooltip={api.matrixData.data?.info}
            id={id}
            titleComponents={[
                !widgetId && (
                    <div className={classes.filters} key={'ListWrapper'}>
                        <div className={classes.filtersWrapper}>
                            <div className={classes.filterTitle}>{`${filtersTitle}:`}</div>
                            <List<string>
                                onChange={handleFilterClick}
                                options={PRIORITIES_MATRIX_FILTERS}
                                value={urgentFilter}
                            />
                        </div>
                    </div>
                ),
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <SubProgramPreferencesLink key={'subProgramPreferences'} />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : null,
            ]}
        >
            {api.matrixData.isLoading && !api.matrixData.data && (
                <LoaderContainer height={PRIORITIES_MATRIX_COMPONENT_HEIGHT}>
                    <OverlayWithSpinner />
                </LoaderContainer>
            )}
            {!api.matrixData.isLoading && !api.matrixData.data && <NoData />}
            {api.matrixData.data && (
                <PrioritiesMatrix
                    buttons={api.matrixData.data}
                    cards={api.cards.data}
                    isCardsLoading={api.cards.isLoading}
                    fetchCards={fetchCardsData}
                    setFilterTitle={setFilterTitle}
                    selected={selectedTypeAndRole}
                    setSelected={setSelectedTypeAndRole}
                    isOverdueFilter={isOverdueFilter}
                    setIsOverdueFilter={setIsOverdueFilter}
                    isAssignedToMeFilter={isAssignedToMeFilter}
                    setIsAssignedToMeFilter={setIsAssignedToMeFilter}
                    isDelayDriverFilter={isDelayDriverFilter}
                    setIsDelayDriverFilter={setIsDelayDriverFilter}
                    isCriticalPathFilter={isCriticalPathFilter}
                    setIsCriticalPathFilter={setIsCriticalPathFilter}
                    linkToBattlecards={linkToProjectActivities}
                />
            )}
        </WidgetWithTitle>
    );
};
export default React.memo(PrioritiesMatrixComponent);
